import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Copy, Check, ExternalLink, Send, Trash2, FileText, Image as ImageIcon, MessageSquare, Bot, ListChecks } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectPortalAdmin({ projectId, projectTitle }) {
  const [portalUrl, setPortalUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [data, setData] = useState(null);
  const [chat, setChat] = useState({ messages: [], decisions: [] });
  const [updateForm, setUpdateForm] = useState({ title: "", text: "", preview_url: "" });
  const [posting, setPosting] = useState(false);
  const [decisionText, setDecisionText] = useState("");
  const [savingDecision, setSavingDecision] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const fetchPortalLink = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/admin/projects/${projectId}/portal-link`, {}, { withCredentials: true });
      // Build URL from current origin so the link always matches the user's actual preview/prod URL
      setPortalUrl(`${window.location.origin}/projekt/${r.data.portal_token}`);
      // Also fetch the public portal data so admin sees feedback/files/updates
      const pub = await axios.get(`${API}/projekt/${r.data.portal_token}`);
      setData(pub.data);
      // Also fetch chat history
      try {
        const chatRes = await axios.get(`${API}/projekt/${r.data.portal_token}/chat`);
        setChat(chatRes.data);
      } catch {}
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchPortalLink(); }, [fetchPortalLink]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const postUpdate = async (e) => {
    e.preventDefault();
    if (!updateForm.title.trim() && !updateForm.text.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/admin/projects/${projectId}/updates`, updateForm, { withCredentials: true });
      setUpdateForm({ title: "", text: "", preview_url: "" });
      await fetchPortalLink();
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte posta uppdatering");
    }
    setPosting(false);
  };

  const deleteUpdate = async (updateId) => {
    if (!window.confirm("Ta bort denna uppdatering?")) return;
    await axios.delete(`${API}/admin/projects/${projectId}/updates/${updateId}`, { withCredentials: true });
    await fetchPortalLink();
  };

  const deleteDecision = async (decId) => {
    if (!window.confirm("Ta bort detta beslut från specifikationen?")) return;
    await axios.delete(`${API}/admin/projects/${projectId}/decisions/${decId}`, { withCredentials: true });
    await fetchPortalLink();
  };

  const addDecision = async (e) => {
    e.preventDefault();
    const text = decisionText.trim();
    if (!text) return;
    setSavingDecision(true);
    try {
      await axios.post(`${API}/admin/projects/${projectId}/decisions`, { text, source: "meeting" }, { withCredentials: true });
      setDecisionText("");
      await fetchPortalLink();
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte spara");
    }
    setSavingDecision(false);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    const text = replyText.trim();
    if (!text) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/projects/${projectId}/feedback-reply`, { text }, { withCredentials: true });
      setReplyText("");
      await fetchPortalLink();
    } catch (err) {
      alert(err.response?.data?.detail || "Kunde inte skicka");
    }
    setSendingReply(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 text-[#F59073] animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="project-portal-admin">
      {/* Public link */}
      <div className="bg-[#0A1628] border border-[#F59073]/30 rounded-sm p-4">
        <span className="font-mono text-[10px] text-[#F59073] tracking-[0.15em] block mb-2">PUBLIK PORTAL-LÄNK</span>
        <div className="flex items-center gap-2">
          <input readOnly value={portalUrl} className="flex-1 bg-[#0F1F3A] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm font-mono" data-testid="portal-link-input" />
          <button onClick={copyLink} data-testid="copy-portal-link" className="flex items-center gap-1 bg-[#F59073] text-black px-3 py-2 rounded-sm text-xs font-medium whitespace-nowrap">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Kopierad" : "Kopiera"}
          </button>
          <a href={portalUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 bg-[#1E3050]/40 border border-[#1E3050] text-[#857973] px-3 py-2 rounded-sm text-xs hover:text-white">
            <ExternalLink className="w-3.5 h-3.5" /> Öppna
          </a>
        </div>
        <p className="text-xs text-[#857973] mt-2">Skicka denna länk till kunden så kan de följa projektet, lämna feedback och ladda upp filer.</p>
      </div>

      {/* Post update */}
      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
        <span className="font-mono text-[10px] text-[#857973] tracking-[0.15em] block mb-3">POSTA UPPDATERING TILL KUND</span>
        <form onSubmit={postUpdate} className="space-y-2">
          <input value={updateForm.title} onChange={e => setUpdateForm(p => ({ ...p, title: e.target.value }))}
            placeholder="Rubrik (valfri)" data-testid="update-title"
            className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none" />
          <textarea value={updateForm.text} onChange={e => setUpdateForm(p => ({ ...p, text: e.target.value }))}
            placeholder="Beskriv uppdateringen för kunden…" rows={3} data-testid="update-text"
            className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none resize-none" />
          <input value={updateForm.preview_url} onChange={e => setUpdateForm(p => ({ ...p, preview_url: e.target.value }))}
            placeholder="Ny preview-länk (valfri, https://…)" data-testid="update-preview-url"
            className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#D9A872] outline-none font-mono" />
          <button type="submit" disabled={posting || (!updateForm.title.trim() && !updateForm.text.trim())} data-testid="post-update-btn"
            className="inline-flex items-center gap-2 bg-[#F59073] text-black px-4 py-2 rounded-sm text-sm font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all disabled:opacity-50">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {posting ? "Postar…" : "Posta uppdatering"}
          </button>
        </form>

        {data?.updates?.length > 0 && (
          <div className="mt-5 pt-4 border-t border-[#1E3050] space-y-2">
            <span className="font-mono text-[10px] text-[#857973] tracking-[0.15em]">TIDIGARE UPPDATERINGAR ({data.updates.length})</span>
            {data.updates.map(u => (
              <div key={u.id} className="bg-[#0A1628] border-l-2 border-[#F59073] pl-3 py-2 pr-2 flex items-start justify-between gap-2" data-testid={`admin-update-${u.id}`}>
                <div className="flex-1 min-w-0">
                  {u.title && <p className="text-sm text-white font-medium">{u.title}</p>}
                  {u.text && <p className="text-xs text-neutral-300 whitespace-pre-line mt-0.5">{u.text}</p>}
                  {u.preview_url && <a href={u.preview_url} target="_blank" rel="noreferrer" className="text-[#F59073] text-xs hover:underline mt-1 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> {u.preview_url}</a>}
                  <p className="text-[10px] font-mono text-[#857973] mt-1">{(u.created_at || "").slice(0, 16).replace("T", " ")} &middot; {u.author}</p>
                </div>
                <button onClick={() => deleteUpdate(u.id)} className="text-[#857973] hover:text-red-400 p-1" title="Ta bort">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Decisions / Spec */}
      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
        <span className="font-mono text-[10px] text-[#857973] tracking-[0.15em] flex items-center gap-1.5 mb-3"><ListChecks className="w-3 h-3" /> BESLUT & SPECIFIKATION ({chat.decisions?.length || 0})</span>

        {/* Admin add form */}
        <form onSubmit={addDecision} className="bg-[#0A1628] border border-[#F59073]/20 rounded-sm p-3 mb-3 space-y-2">
          <span className="font-mono text-[10px] text-[#F59073] tracking-[0.15em] block">LÄGG TILL FRÅN MÖTE / ÖVERENSKOMMELSE</span>
          <textarea value={decisionText} onChange={e => setDecisionText(e.target.value)}
            placeholder="T.ex. 'Beslutat på möte 6/5: Hero ska ha bokningskalender direkt, vi levererar wireframes nästa fredag, kunden skickar logotyp i SVG senast tisdag.'"
            rows={3} data-testid="admin-decision-text"
            className="w-full bg-[#0F1F3A] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none resize-none leading-relaxed" />
          <button type="submit" disabled={savingDecision || !decisionText.trim()} data-testid="admin-add-decision-btn"
            className="inline-flex items-center gap-2 bg-[#F59073] text-black px-3 py-1.5 rounded-sm text-xs font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all disabled:opacity-50">
            {savingDecision ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {savingDecision ? "Sparar…" : "Lägg till i specifikation"}
          </button>
        </form>

        {!chat.decisions?.length ? (
          <p className="text-xs text-[#857973]">Inga beslut sparade än.</p>
        ) : (
          <div className="space-y-2">
            {chat.decisions.map((d, i) => (
              <div key={d.id} className="bg-[#0A1628] border-l-2 border-[#F59073] rounded-sm pl-3 pr-2 py-2 flex items-start justify-between gap-2" data-testid={`admin-decision-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[10px] text-[#F59073]">#{String(i + 1).padStart(2, "0")}</span>
                    {d.by_admin && <span className="font-mono text-[9px] bg-[#F59073]/20 text-[#F59073] px-1.5 py-0.5 rounded-sm tracking-wider">FRÅN MÖTE</span>}
                    <span className="text-[10px] font-mono text-[#857973]">{d.saved_by || "Kund"} &middot; {(d.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <p className="text-sm text-neutral-200 whitespace-pre-line">{d.text}</p>
                </div>
                <button onClick={() => deleteDecision(d.id)} className="text-[#857973] hover:text-red-400 p-1 shrink-0" title="Ta bort">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI chat history */}
      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
        <span className="font-mono text-[10px] text-[#857973] tracking-[0.15em] flex items-center gap-1.5 mb-3"><Bot className="w-3 h-3" /> AI-BOLLPLANK · CHATT-HISTORIK ({chat.messages?.length || 0})</span>
        {!chat.messages?.length ? (
          <p className="text-xs text-[#857973]">Ingen chatt-aktivitet än.</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {chat.messages.map(m => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-sm px-3 py-2 ${m.role === "user" ? "bg-[#F59073]/10 border border-[#F59073]/30 text-neutral-100" : "bg-[#0A1628] border border-[#1E3050] text-neutral-200"}`} data-testid={`admin-chat-${m.role}`}>
                  <p className="text-[10px] font-mono mb-1 opacity-60">{m.role === "user" ? "KUND" : "AI"} &middot; {(m.created_at || "").slice(11, 16)}</p>
                  <p className="text-sm leading-relaxed whitespace-pre-line">{m.text}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Customer feedback thread (two-way) */}
      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
        <span className="font-mono text-[10px] text-[#857973] tracking-[0.15em] flex items-center gap-1.5 mb-3"><MessageSquare className="w-3 h-3" /> KONVERSATION MED KUND ({data?.feedback?.length || 0})</span>
        {!data?.feedback?.length ? (
          <p className="text-xs text-[#857973] mb-4">Ingen aktivitet än.</p>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1 mb-4">
            {data.feedback.map(f => {
              const isAdmin = f.role === "admin";
              return (
                <div key={f.id} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-sm px-3 py-2 ${isAdmin ? "bg-[#F59073]/15 border border-[#F59073]/30 text-neutral-100" : "bg-[#0A1628] border border-[#1E3050] text-neutral-200"}`} data-testid={`admin-feedback-${f.id}`}>
                    <p className="text-[10px] font-mono mb-1 opacity-70">{isAdmin ? "WEDOWEDO" : (f.author_name || "KUND")} &middot; {(f.created_at || "").slice(0, 16).replace("T", " ")}</p>
                    <p className="text-sm leading-relaxed whitespace-pre-line">{f.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {/* Admin reply form */}
        <form onSubmit={sendReply} className="space-y-2 pt-3 border-t border-[#1E3050]">
          <span className="font-mono text-[10px] text-[#F59073] tracking-[0.15em] block">SVARA KUNDEN</span>
          <textarea value={replyText} onChange={e => setReplyText(e.target.value)}
            placeholder="Skriv ditt svar… kunden får ett mejl med länk till portalen."
            rows={3} data-testid="admin-reply-text"
            className="w-full bg-[#0A1628] border border-[#1E3050] text-white px-3 py-2 rounded-sm text-sm focus:border-[#F59073] outline-none resize-none leading-relaxed" />
          <button type="submit" disabled={sendingReply || !replyText.trim()} data-testid="admin-send-reply-btn"
            className="inline-flex items-center gap-2 bg-[#F59073] text-black px-3 py-1.5 rounded-sm text-xs font-medium hover:shadow-[0_0_15px_rgba(245,144,115,0.3)] transition-all disabled:opacity-50">
            {sendingReply ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sendingReply ? "Skickar…" : "Skicka svar"}
          </button>
        </form>
      </div>

      {/* Customer files */}
      <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-4">
        <span className="font-mono text-[10px] text-[#857973] tracking-[0.15em] flex items-center gap-1.5 mb-3"><ImageIcon className="w-3 h-3" /> KUNDENS UPPLADDADE FILER ({data?.files?.length || 0})</span>
        {!data?.files?.length ? (
          <p className="text-xs text-[#857973]">Inga filer uppladdade än.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {[...data.files].reverse().map(f => {
              const isImg = (f.content_type || "").startsWith("image/") || /\.(jpe?g|png|gif|webp|svg)$/i.test(f.filename || "");
              return (
                <a key={f.id} href={f.url} target="_blank" rel="noreferrer" data-testid={`admin-file-${f.id}`}
                  className="block bg-[#0A1628] border border-[#1E3050] rounded-sm overflow-hidden hover:border-[#F59073]/40">
                  {isImg ? (
                    <img src={f.url} alt={f.filename} className="w-full aspect-square object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full aspect-square flex items-center justify-center"><FileText className="w-7 h-7 text-[#857973]" /></div>
                  )}
                  <div className="p-2">
                    <p className="text-[11px] text-white truncate">{f.filename}</p>
                    <p className="text-[10px] text-[#857973]">{((f.size || 0) / 1024).toFixed(0)} KB</p>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
